import {
	Label,
	MessageBar,
	MessageBarType,
	Pivot,
	PivotItem,
	Stack,
	StackItem
} from '@fluentui/react';
import React from 'react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import SearchCtrl, { SearchCtrlStyle } from '../components/controls/SearchCtrl';
import {
	EntityAPI,
	IEntityTypeUserInfo,
	IGenericEntity,
	IUserConfigPackage
} from '../API';
import LegalEntityDocumentsStateOutlook, {
	IEmailStoredFilingInfo,
	IOutlookAppCommands,
	OutlookMessageMode,
} from '../state/CorporateArchiveStates/LegalEntityDocumentsStateOutlook';
import { CombinedOutlookLocationViewer } from './CombinedSearchResultsStateOutlook';

import OutlookFileDocumentsDlg, { CompletionStatus, OutlookFileDocumentsState } from './OutlookFileDocumentsDlg';
import OutlookItemCommandBar from './OutlookItemCommandBar';
import CombinedSearchResultsEntityNav from './CombinedSearchResultsEntityNav';
import OutlookTabPinnedItems, { OutlookTabPinnedItemsState } from './OutlookTabPinnedItems';
import { OutlookTabSuggestedState } from './OutlookTabSuggested';
import OutlookTabRecent, { OutlookTabRecentState } from './OutlookTabRecent';
import { EntityNavigatorState } from '../components/controls/EntityNavigator';
import OutlookLocationContentsViewerState from '../state/CorporateArchiveStates/LegalEntityDocumentsStateOutlook';
import LegalEntityRootFolder from './LegalEntityRootFolder';

export interface IMailProps {
	state: MobileAttachmentsState;
}

class MobileAttachments extends React.Component<IMailProps, {}> {

	render(): React.ReactNode {

		return (
			<>
				{this.props.state.uploadAndCreateNewState && <OutlookFileDocumentsDlg state={this.props.state.uploadAndCreateNewState} />}

				<Stack style={{ width: '100%' }}>
					<StackItem>
						<OutlookItemCommandBar state={this.props.state} />
					</StackItem>

					<StackItem style={{ marginBottom: 10 }}>
						<Stack horizontal>
							<StackItem grow>
								<SearchCtrl
									initialValue={''}
									style={SearchCtrlStyle.Underlined}
									onSearchTextChanged={this.props.state.search.bind(this.props.state)}
									placeholder={'Search...'}
								/>
							</StackItem>
						</Stack>
					</StackItem>
					<StackItem>{this.getNewlyCreatedLocationInfo()}</StackItem>
					<StackItem>{this.getFilingInfo()}</StackItem>
					<StackItem>{this.getDocumentContent()}</StackItem>
				</Stack>
			</>
		);
	}

	getNewlyCreatedLocationInfo(): React.ReactNode {
		if (this.props.state.newlyCreatedLocationState) {
			return <Stack>
				<MessageBar messageBarType={MessageBarType.success}>
					The new item has been created successfully.
				</MessageBar>

				{this.props.state.newlyCreatedLocationState && <LegalEntityRootFolder state={this.props.state.newlyCreatedLocationState} />}

			</Stack>
		}
	}

	getFilingInfo(): React.ReactNode {
		if (this.props.state.hasBeenFiled && this.props.state.filingInfo?.filingMessage) {
			return <Stack>
				<MessageBar messageBarType={MessageBarType.success}>
					{this.props.state.filingInfo.filingMessage}
				</MessageBar>

				{this.props.state.filedLocationState && <LegalEntityRootFolder state={this.props.state.filedLocationState} />}

			</Stack>
		} else if(this.props.state.isSendAndFile) {
			return <Stack>
				<MessageBar messageBarType={MessageBarType.info}>
					Select a location to file this email to.
				</MessageBar>
			</Stack>
		}
	}

	getDocumentContent(): React.ReactNode {

		return (
			<Pivot
				selectedKey={this.props.state.pivot}
				onLinkClick={(p) => {
					this.props.state.setSelectedPivot(p?.props.itemKey || '');
				}}
			>
				{this.props.state.searchResult.searchText ? (
					<PivotItem itemKey="search" headerText="Search Results">
						{this.getSearchResults()}
					</PivotItem>
				) : (
					<></>
				)}

				{/*
				<PivotItem itemKey="suggested" headerText="Suggested">
					<OutlookTabSuggested state={this.props.state.suggestedTab} />
				</PivotItem>
				*/}

				<PivotItem itemKey="favourites" headerText="Favorites">
					<OutlookTabPinnedItems state={this.props.state.pinnedTab} />
				</PivotItem>

				{this.props.state.messageMode === OutlookMessageMode.Compose && (
					<PivotItem itemKey="recent" headerText="Recent">
						<OutlookTabRecent state={this.props.state.recentDocs} />
					</PivotItem>
				)}
			</Pivot>
		);
	}

	getSearchResults(): React.ReactNode {

		let noResults = !this.props.state.searchResult.loading &&
			!this.props.state.searchResult.allItems?.any &&
			!this.props.state.searchResult.orphanResults.documentStates.length;


		if (noResults) {

			if (this.props.state.searchResult.searchText) {
				return <Label>No results found.</Label>;
			} else {
				return <Label>Search for a filing location to start.</Label>;
			}
		}

		return (
			<Stack>
				<CombinedSearchResultsEntityNav state={this.props.state.searchResult} />
			</Stack>
		);
	}
}

export default observer(MobileAttachments);

export class MobileAttachmentsState {

	public searchResult: CombinedOutlookLocationViewer;

	public pinnedTab: OutlookTabPinnedItemsState;
	public suggestedTab: OutlookTabSuggestedState;
	public recentDocs: OutlookTabRecentState;

	userConfig: IUserConfigPackage;

	public messageMode: OutlookMessageMode = OutlookMessageMode.None;

	public commands: IOutlookAppCommands;

	public uploadAndCreateNewState?: OutlookFileDocumentsState;

	public filingInfo?: IEmailStoredFilingInfo;
	public filedLocationState?: LegalEntityDocumentsStateOutlook;
	public newlyCreatedLocationState?: LegalEntityDocumentsStateOutlook;
	public hasBeenFiled: boolean = false;
	public mode: OutlookMessageMode = OutlookMessageMode.None;

	private currentItemPropsLoaded: boolean = false;

	public pivot: string = 'suggested';

	constructor(userConfig: IUserConfigPackage, commands: IOutlookAppCommands) {
		this.userConfig = userConfig;
		this.commands = commands;

		this.pinnedTab = new OutlookTabPinnedItemsState(userConfig, commands);
		let ctxCommands = this.pinnedTab.getCtxCommands();

		this.suggestedTab = new OutlookTabSuggestedState(userConfig, commands, ctxCommands);
		this.recentDocs = new OutlookTabRecentState(userConfig, commands, ctxCommands);

		this.searchResult = new CombinedOutlookLocationViewer(
			userConfig,
			commands,
			ctxCommands
		);

		this.commands.onItemChanged(async () => {
			this.currentItemPropsLoaded = false;
			await this.loadCurrentMessageProps();
		});

		makeAutoObservable(this);

		if (commands) {
			commands.onItemChanged(this.updateSuggested.bind(this));
			commands.onItemInfoChanged(this.updateSuggested.bind(this));
			this.messageMode = commands.getMessageMode();
		}

		this.commands.getInitializationContext().then(async (ctx) => {
			if(ctx?.activity === 'configureSendAndFile') {
				this.setIsSendAndFileMode(true);
			}
		});
	}

	public isSendAndFile: boolean = false;
	public setIsSendAndFileMode(isSendAndFile: boolean) {
		this.isSendAndFile = isSendAndFile;
	}

	public async showCreateEntityDialog(entityType: IEntityTypeUserInfo) {

		//Create url parameters to pass the message context to the new item dialog
		let params = new URLSearchParams();

		let messageInfo = await this.commands.getActiveItemInfo();

		params.set("helmUX", "1");
		params.set("Subject", messageInfo.subject || '');
		params.set("From", messageInfo.from?.emailAddress || '');
		params.set("Source", "https://" + window.location.hostname + "/requestcomplete");
		let paramString = params.toString();

		if (entityType.sharePointListNewFormUrl.indexOf('?') >= 0) {
			paramString = "&" + paramString;
		} else {
			paramString = "?" + paramString;
		}

		this.commands.launchWindow(entityType.sharePointListNewFormUrl + paramString, async () => {
			//Get the last item created by the current user
			let accessToken = await this.userConfig.getToken();
			let api = await EntityAPI.fromEntityTypeId(entityType.id, accessToken) as EntityAPI<IGenericEntity>;
			let itemId = await api.getLastUserCreatedItemId();
			if (itemId) {
				let entity = await api.item(itemId, null);
				if (entity) {
					this.setNewlyCreatedLocationState(EntityNavigatorState.createState(
						entityType.id,
						entity,
						this.userConfig,
						this.commands,
						this.pinnedTab.getCtxCommands()
					));
				}
			}
		});
	}

	public async showUploadAndCreateEntityDialog(entityType: IEntityTypeUserInfo) {
		this.uploadAndCreateNewState = new OutlookFileDocumentsState(entityType.id, this.userConfig, this.commands);

		let currentMessage = await this.commands.getActiveItemInfo();
		let currentAttachments = await this.commands.getAttachments();

		this.uploadAndCreateNewState.setVisible(true, '', '', 'new entity', currentMessage, currentAttachments, (s: CompletionStatus) => {
			if (s === CompletionStatus.AllSuccess || s === CompletionStatus.PartialSuccess) {
				this.updateSuggested();
			}
		});
	}

	private updateSuggested = async () => {
		//await this.suggestedTab.updateSuggested();
	}

	public showViewAllEntityDialog(entityType: IEntityTypeUserInfo) {
		this.commands.launchWindow(entityType.sharePointListUrl + "?helmUX=1");
	}

	public setSelectedPivot(key: string) {
		this.pivot = key;
	}

	public async search(text: string): Promise<void> {
		this.searchResult.setSearchText(text);
		this.setSelectedPivot('search');
	}

	public async loadCurrentMessageProps(): Promise<void> {
		if (!this.currentItemPropsLoaded) {
			this.currentItemPropsLoaded = true;
			let customProps = await this.commands.getMessageCustomProps();
			this.setFilingInfo(customProps);
			this.commands.onMessageCustomPropsUpdated(this.setFilingInfo.bind(this));
		}
	}

	private setFilingInfo(info?: IEmailStoredFilingInfo) {
		this.mode = this.commands.getMessageMode();
		this.hasBeenFiled = !!info?.filingMessage;
		this.filingInfo = info;
		this.loadFiledOutlookLocationState();
	}

	async loadFiledOutlookLocationState() {

		let set = false;

		if (this.filingInfo?.entityType && this.filingInfo?.entityId) {

			//Load the entity info
			let entityTypeInfo = this.userConfig.entityTypes.getConfig(this.filingInfo.entityType);
			if (entityTypeInfo) {
				let accessToken = await this.userConfig.getToken();
				let api = await EntityAPI.fromEntityTypeId(entityTypeInfo.id, accessToken) as EntityAPI<IGenericEntity>;
				let entity = await api.item(this.filingInfo.entityId, null);
				if (entity) {
					this.setOutlookFiledLocationState(EntityNavigatorState.createState(
						entityTypeInfo.id,
						entity,
						this.userConfig,
						this.commands,
						this.pinnedTab.getCtxCommands()
					));
					set = true;
				}
			}
		}

		if(!set && this.filingInfo?.filedUrl) {

			let serverRelativeUrl = this.filingInfo?.filedUrl || '';
			if(serverRelativeUrl.startsWith("https://")) {
				serverRelativeUrl = new URL(serverRelativeUrl).pathname;

				//Remove the filename from the end
				serverRelativeUrl = serverRelativeUrl.substring(0, serverRelativeUrl.lastIndexOf('/'));
			}

			this.setOutlookFiledLocationState(EntityNavigatorState.createState(
				'',
				{
					DisplayTitle: serverRelativeUrl,
					ServerRelativeUrl: this.filingInfo?.filedUrl || '',
				} as IGenericEntity,
				this.userConfig,
				this.commands,
				this.pinnedTab.getCtxCommands()
			));
		}
	}

	public setNewlyCreatedLocationState(state: OutlookLocationContentsViewerState) {
		this.newlyCreatedLocationState = state;
	}

	public setOutlookFiledLocationState(state: OutlookLocationContentsViewerState) {
		this.filedLocationState = state;
	}
}
